import {Alert, Button} from 'platform/components';
import {Box, Heading, HStack, Show, Stack, Text, VStack} from 'platform/foundation';
import {useLocale} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetVehicleQuery} from '@dms/api/sales';
import {FullVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {urlBuilder} from '@dms/routes';
import {createSearchParamsString} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

const REQUIRED_VEHICLE_FIELDS: {
  i18nKey: string;
  getValue: (v: FullVehicleResponseBody) => unknown;
}[] = [
  {i18nKey: 'entity.vehicle.labels.body', getValue: (v) => v.bodyStyle},
  {i18nKey: 'entity.vehicle.labels.engineCapacity', getValue: (v) => v.engine?.engineVolume},
  {i18nKey: 'entity.vehicle.labels.transmission', getValue: (v) => v.transmission},
  {i18nKey: 'entity.vehicle.labels.fuel', getValue: (v) => v.fuelType},
  {i18nKey: 'entity.vehicle.labels.modelYear', getValue: (v) => v.manufacturedOnYear},
  {i18nKey: 'entity.vehicle.labels.mileage', getValue: (v) => v.vehicle.mileage},
];

function getMissingVehicleFieldLabels(vehicle: FullVehicleResponseBody): string[] {
  return REQUIRED_VEHICLE_FIELDS.filter((field) => isNil(field.getValue(vehicle))).map((field) =>
    i18n.t(field.i18nKey)
  );
}

interface MiraPredictionWidgetProps extends TestIdProps {
  vehicleId: string;
}

export function MiraPredictionWidget(props: MiraPredictionWidgetProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const {language} = useLocale();

  if (isNil(vehicle?.make) || isNil(vehicle?.modelFamily)) {
    return null;
  }

  const missingFieldLabels = getMissingVehicleFieldLabels(vehicle);
  const hasMissingFields = missingFieldLabels.length > 0;

  const miraDomain = process.env.MIRA_DOMAIN;
  const isMiraDomainUnconfigured = isNil(miraDomain);

  const handleOpenMira = () => {
    if (isNil(miraDomain)) {
      return;
    }

    const miraUrl = urlBuilder({
      protocol: window.location.protocol,
      workspace: undefined,
      domain: miraDomain,
      route: '/report',
      searchParam: createSearchParamsString({
        vin: vehicle?.vin ?? undefined,
        make: vehicle?.make ?? undefined,
        modelFamily: vehicle?.modelFamily ?? undefined,
        bodyStyle: vehicle?.bodyStyle ?? undefined,
        engineVolume: vehicle?.engine?.engineVolume?.toString() ?? undefined,
        transmission: vehicle?.transmission ?? undefined,
        fuelType: vehicle?.fuelType ?? undefined,
        modelYear: vehicle?.manufacturedOnYear?.toString() ?? undefined,
        firstRegistration: vehicle?.vehicle.firstRegistrationOn ?? undefined,
        mileage: vehicle?.vehicle.mileage?.toString() ?? undefined,
        lang: language,
      }),
    });
    window.open(miraUrl, '_blank')?.focus();
  };

  return (
    <VStack spacing={2}>
      <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" overflow="hidden">
        <Stack direction={['column', 'row']}>
          <MiraLogo />
          <Box padding={4} width="100%" backgroundColor="palettes.neutral.10.100">
            <VStack spacing={3}>
              <Heading size={4}>{i18n.t('page.integrations.labels.miraPredictionTitle')}</Heading>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.integrations.labels.miraPredictionDescription')}
              </Text>
              <HStack spacing={2}>
                <Button
                  data-testid={suffixTestId('miraPredictionWidget-openReport', props)}
                  variant="secondary"
                  rightIcon="action/launch"
                  isDisabled={hasMissingFields || isMiraDomainUnconfigured}
                  onClick={handleOpenMira}
                  title={i18n.t('page.integrations.labels.miraPredictionOpenReport')}
                />
              </HStack>
            </VStack>
          </Box>
        </Stack>
      </Box>
      <Show when={hasMissingFields}>
        <Alert
          type="inline"
          variant="warning"
          message={`${i18n.t('page.integrations.labels.miraPredictionMissingFields')}: ${missingFieldLabels.join(', ')}`}
        />
      </Show>
    </VStack>
  );
}

const MIRA_LOGO_STYLE = {
  color: 'white',
  fontSize: '28px',
  fontWeight: 700,
  letterSpacing: '0.2em',
} as const;

function MiraLogo() {
  return (
    <Box backgroundColor="palettes.neutral.900.100" padding={4} minWidth={39}>
      <VStack justify="center" align="center" height="100%">
        <span style={MIRA_LOGO_STYLE}>MIRA</span>
      </VStack>
    </Box>
  );
}
